import { useMutation, useQuery } from "@apollo/client/react";
import {
  DSCardWrapper,
  DSLinkButton,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LOG_SCRIBEPHONE_CONSENT } from "@graphql/mutations/ScribephoneConsent";
import { GET_DEFAULT_PHONE_NUMBER } from "@graphql/queries/DefaultPhoneNumber";
import { GET_SCRIBEPHONE_CONSENT } from "@graphql/queries/ScribephoneConsent";
import {
  useCurrentUser,
  usePremiumFeature,
  useTwilioAPI,
  useUserActivations,
  useVerifiedPhoneNumbers,
  useWatchUserWsEvent,
  useVoIP,
} from "@hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import PhoneSettingsStep from "./PhoneVerificationModal";
import CallStatusStep from "./steps/CallStatusStep";
import ConsentStep from "./steps/ConsentStep";
import DialStep from "./steps/DialStep";
import ScribephoneUnactivatedState from "./UnactivatedState";
import { detectCountry, SCRIBEPHONE_CONSENT_MODES } from "./utils";
import {
  canAcceptMoreDigits,
  formatPhoneNumberForInput,
  getDigitsOnly,
} from "../../../utils/phoneUtils";
import { alert } from "../../common/Alert";
import { parseJobType, JOB_TYPE_KIND } from "../../dashboard/utils";

export default function ScribephoneDial({
  shouldShow,
  setShouldShow,
  selectedJobType,
  recordingAs,
  control,
  patientUUID = null,
  appendToNoteUuid = null,
  recordingConsentEventUuid = null,
}) {
  // Hooks and API
  const navigate = useNavigate();
  const { initiateCall, endCall } = useTwilioAPI();
  const {
    data: currentUser,
    loading: userLoading,
    refetch,
  } = useCurrentUser();
  const { UserPermissions, hasFeatureAccess } = usePremiumFeature();
  // TODO(WEB-1862): Remove releaseWebEnableScribephoneDtmf after FF cleanup
  const {
    isPhoneVerificationEnabled,
    releaseWebEnableScribephoneDtmf,
  } = useFlags();
  const [logConsent] = useMutation(LOG_SCRIBEPHONE_CONSENT);
  const {
    verifiedPhoneNumbers,
    loading: phonesLoading,
    refetch: refetchVerifiedPhones,
  } = useVerifiedPhoneNumbers();
  const { isScribephoneActivated, refetchActivations } =
    useUserActivations();

  const { data: consentData } = useQuery(GET_SCRIBEPHONE_CONSENT);

  // State
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isDialing, setIsDialing] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [callSid, setCallSid] = useState("");
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState("dial");
  const [isMuted, setIsMuted] = useState(false);
  const [activeConferenceName, setActiveConferenceName] =
    useState("");
  const [selectedCallerId, setSelectedCallerId] = useState(
    () => localStorage.getItem("scribephoneCallerId") || "default",
  );
  const [userCountry] = useState(detectCountry);
  const [showUnactivatedState, setShowUnactivatedState] =
    useState(true);

  const { data: defaultPhoneData } = useQuery(
    GET_DEFAULT_PHONE_NUMBER,
    {
      variables: { countryCode: userCountry },
    },
  );
  const defaultPhoneNumber = defaultPhoneData?.defaultPhoneNumber;

  // Computed values
  const hasPhoneVerificationAccess =
    isPhoneVerificationEnabled &&
    hasFeatureAccess(UserPermissions.SCRIBEPHONE_ENABLED);
  const hasOptedIn =
    currentUser?.user?.scribephoneConsentOptIn || false;

  // Ref to store cleanup function
  const cleanupRef = useRef();

  // Utility functions (moved up to fix lexical declaration error)
  const clearCallState = () => {
    setPhoneNumber("");
    setIsDialing(false);
    setCallStatus(null);
    setCallSid(null);
    setError(null);
    setCurrentStep("dial");
    setActiveConferenceName(null);
    setShowUnactivatedState(true);
    setIsMuted(false);
  };

  const handleAppendModeNavigation = () => {
    if (appendToNoteUuid) {
      alert(
        "success",
        "Audio file uploaded! Taking you back to the dashboard...",
      );
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    }
  };

  const handleCallDisconnected = () => {
    setCallStatus("Call ended.");
    setTimeout(() => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
      handleAppendModeNavigation();
    }, 500);
  };

  // VoIP setup
  const {
    initialize: initializeVoIP,
    joinConference,
    disconnect: disconnectVoIP,
    destroy: destroyVoIP,
    getCurrentCall,
    isConnected: isVoIPConnected,
  } = useVoIP(handleCallDisconnected);

  // Update cleanup function after useVoIP hook
  const finalCleanupAndClose = () => {
    setShouldShow(false);
    clearCallState();
    destroyVoIP();
  };

  // Store the cleanup function in ref
  cleanupRef.current = finalCleanupAndClose;

  // Effects
  useEffect(() => {
    if (shouldShow) {
      setCurrentStep("dial");
      setError(null);
    }
  }, [shouldShow]);

  useEffect(() => {
    // Only validate localStorage when we have loaded the verified phones data
    if (!phonesLoading && !userLoading) {
      const storedCallerId = localStorage.getItem(
        "scribephoneCallerId",
      );
      if (storedCallerId && storedCallerId !== "default") {
        const hasAccessToNumber = verifiedPhoneNumbers.some(
          (phone) => phone.phoneNumber === storedCallerId,
        );
        if (!hasAccessToNumber) {
          localStorage.setItem("scribephoneCallerId", "default");
          setSelectedCallerId("default");
        }
      }
    }
  }, [verifiedPhoneNumbers, phonesLoading, userLoading]);

  // WebSocket event handling
  const handleClientJoined = () => {
    setCallStatus("Connected! Both parties are now in the call.");
    if (!releaseWebEnableScribephoneDtmf) {
      setCurrentStep("connected");
    }
  };

  const handleWebSocketMessage = (data) => {
    if (data.event === "client_join_call") {
      handleClientJoined();
    } else if (
      data.event === "client_call_updated" &&
      data.data.status === "completed" &&
      currentStep === "waiting_for_client"
    ) {
      handleCallDisconnected();
    }
  };

  useWatchUserWsEvent({
    onMessage: handleWebSocketMessage,
  });

  // Utility functions
  const trackCallEvent = (eventName, additionalData = {}) => {
    mixpanel.track(eventName, {
      timestamp: new Date().toISOString(),
      jobType: selectedJobType?.value,
      recordingAs: recordingAs,
      isAppendingToNote: !!appendToNoteUuid,
      ...additionalData,
    });
  };

  const handleDTMFSent = (digit) => {
    trackCallEvent("DTMF Sent", { digit });
  };

  // Event handlers
  const updatePhoneDigits = (transform) => {
    setPhoneNumber((prev) => {
      return formatPhoneNumberForInput(
        transform(getDigitsOnly(prev)),
      );
    });
  };

  const handleNumberPress = (number) => {
    if (canAcceptMoreDigits(phoneNumber)) {
      updatePhoneDigits((digits) => digits + number);
    }
  };

  const handleInputChange = (formatted) => {
    setPhoneNumber(formatted);
  };

  const handleEnterPress = () => {
    if (phoneNumber.trim() && !isDialing) {
      handleCallClient(false);
    }
  };

  const handleBackspace = () => {
    updatePhoneDigits((digits) => digits.slice(0, -1));
  };

  const handleConsentContinue = async (rememberDecision) => {
    try {
      if (rememberDecision) {
        const result = await logConsent();
        if (!result.data?.logScribephoneConsent?.ok) {
          setError(
            result.data?.logScribephoneConsent?.error ||
              "Failed to log consent",
          );
          return;
        }
        refetch();
      }
      await handleCallClient(true);
    } catch (error) {
      setError("Failed to log consent decision");
      console.error("Consent logging error:", error);
    }
  };

  const handleConsentBack = () => {
    setCurrentStep("dial");
  };

  const handleCallerIdSelected = (callerId) => {
    setSelectedCallerId(callerId);
    localStorage.setItem("scribephoneCallerId", callerId);
    setError(null);
  };

  const handleCallClient = async (hasReadConsent = false) => {
    if (!phoneNumber.trim()) return;

    setError(null);
    if (
      !hasOptedIn &&
      !userLoading &&
      !hasReadConsent &&
      consentData?.user?.scribephoneConsentMode !=
        SCRIBEPHONE_CONSENT_MODES.AUTOMATIC
    ) {
      setCurrentStep("consent");
      return;
    }

    try {
      const permissionStream =
        await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the permission check stream immediately - we only needed it to verify access
      permissionStream.getTracks().forEach((track) => track.stop());
    } catch {
      setError(
        "Microphone access is required to make a call. Please enable mic permissions in your browser settings.",
      );
      return;
    }

    setIsDialing(true);
    setCallStatus("Calling client...");
    if (releaseWebEnableScribephoneDtmf) {
      setCurrentStep("dial");
    } else {
      setCurrentStep("calling");
    }

    try {
      const parsed = parseJobType(selectedJobType?.value);
      const callMetadata = {
        jobType: parsed.jobType,
        recordingAs: recordingAs,
        noteTitle: control?._formValues?.title || "",
        patientUuid: patientUUID,
        appendToNoteUuid: appendToNoteUuid,
        recordingConsentEventUuid,
      };

      if (parsed.kind === JOB_TYPE_KIND.TEMPLATE_GROUP) {
        callMetadata.jobType = "template_group";
        callMetadata.templateGroupUuid = parsed.templateGroupUuid;
      } else if (parsed.kind === JOB_TYPE_KIND.TEMPLATE) {
        callMetadata.templateUuid = parsed.templateUuid;
      }

      const result = await initiateCall(
        phoneNumber,
        callMetadata,
        userCountry,
        selectedCallerId === "default" ? undefined : selectedCallerId,
      );

      await initializeVoIP(result.identity, result.token);

      setActiveConferenceName(result.conference_name);
      trackCallEvent("Call Initiated", {
        conferenceName: result.conference_name,
      });

      refetchActivations();

      await joinConference(result.conference_name);
      setCallSid(result.call_sid);
      setCallStatus("Ringing... Waiting for client to answer.");
      if (!releaseWebEnableScribephoneDtmf) {
        setCurrentStep("waiting_for_client");
      }
    } catch (error) {
      if (
        error.message ===
        "The selected phone number does not belong to your account. Please select a different number or use the default."
      ) {
        setSelectedCallerId("default");
        localStorage.setItem("scribephoneCallerId", "default");
        refetchVerifiedPhones();
        alert(
          "error",
          "The phone number which you are trying to call from is not properly verified. You have been reset to the default number. Please re-verify this number.",
        );
      } else {
        setError(
          error.message ||
            "Failed to place call. Please check the number and try again.",
        );
      }
      setCallStatus(null);
      setCurrentStep("dial");
      trackCallEvent("Call Failed", {
        error: error.message || "Unknown error",
      });
    } finally {
      setIsDialing(false);
    }
  };

  const handleEndCall = async () => {
    setError(null);
    setIsDialing(true);
    setCallStatus("Ending call...");

    try {
      if (isVoIPConnected) {
        disconnectVoIP();
      }

      if (callSid) {
        await endCall(callSid);
      }

      trackCallEvent("Call Ended", {
        conferenceName: activeConferenceName,
      });

      setCallStatus("Call ended.");
    } catch (error) {
      console.error("End call failed:", error);
      setCallStatus(null);
      setError(error.message || "Failed to end call.");
      trackCallEvent("Call End Failed", {
        error: error.message || "Unknown error",
        callSid: callSid,
      });
    } finally {
      setIsDialing(false);
      setTimeout(finalCleanupAndClose, 800);
    }
  };

  const toggleMute = () => {
    const call = getCurrentCall();
    if (call) {
      if (isMuted) {
        call.mute(false);
      } else {
        call.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const handleClose = async (shouldCleanup = true) => {
    if (callSid || isVoIPConnected) {
      await handleEndCall();
      return;
    }

    if (shouldCleanup) {
      finalCleanupAndClose();
    } else {
      setShouldShow(false);
    }
  };

  const handleModalClose = () => handleClose(false);

  const handleOpenSettings = () => {
    setCurrentStep("settings");
  };

  // Render helpers
  const renderCallerIdButton = () =>
    hasPhoneVerificationAccess && (
      <DSLinkButton
        onClick={() => setCurrentStep("settings")}
        title="Scribephone Settings"
      >
        Calling from:{" "}
        <span className="ml-[2px]">
          {selectedCallerId === "default"
            ? defaultPhoneNumber
            : selectedCallerId}
        </span>
      </DSLinkButton>
    );

  const renderCallStatusStep = (status) => (
    <CallStatusStep
      handleEndCall={handleEndCall}
      toggleMute={toggleMute}
      isMuted={isMuted}
      isDialing={isDialing}
      status={status}
    />
  );

  const renderContent = () => {
    if (!isScribephoneActivated && showUnactivatedState) {
      return (
        <ScribephoneUnactivatedState
          onDismiss={() => setShowUnactivatedState(false)}
        />
      );
    }
    const isInActiveCall =
      releaseWebEnableScribephoneDtmf && (isVoIPConnected || callSid);

    switch (currentStep) {
      case "dial":
        return (
          <DialStep
            setCurrentStep={setCurrentStep}
            phoneNumber={phoneNumber}
            handleNumberPress={handleNumberPress}
            handleInputChange={handleInputChange}
            handleEnterPress={handleEnterPress}
            handleBackspace={handleBackspace}
            handleCallClient={handleCallClient}
            isDialing={isDialing}
            onOpenSettings={handleOpenSettings}
            renderCallerIdButton={renderCallerIdButton}
            hasPhoneVerificationAccess={hasPhoneVerificationAccess}
            isConnected={isInActiveCall}
            getCurrentCall={getCurrentCall}
            onEndCall={handleEndCall}
            toggleMute={toggleMute}
            isMuted={isMuted}
            onDTMFSent={handleDTMFSent}
            callStatus={callStatus}
          />
        );
      case "consent":
        return (
          <ConsentStep
            setCurrentStep={setCurrentStep}
            onContinue={handleConsentContinue}
            onBack={handleConsentBack}
          />
        );
      case "waiting_for_client":
        return renderCallStatusStep("waiting");
      case "connected":
        return renderCallStatusStep("connected");
      case "settings":
        return hasPhoneVerificationAccess ? (
          <PhoneSettingsStep
            onClose={() => setCurrentStep("dial")}
            onCallerIdChange={handleCallerIdSelected}
            defaultPhoneNumber={defaultPhoneNumber}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-secondary">
              Phone settings is not available.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <DSModal
      isOpen={shouldShow}
      onClose={() => {
        setShouldShow(false);
        handleModalClose();
      }}
      title="Scribephone"
      width="w-[400px]"
      dataTestId="scribephone-modal"
      dismissible={true}
      disableBackdropClose
    >
      <div className="flex flex-col space-y-4 pb-1">
        {renderContent()}

        <div className="w-full text-center py-1">
          {error ? (
            <DSCardWrapper variant="danger">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="text-danger mr-2"
                />
                <p className="text-danger text-xs">{error}</p>
              </div>
            </DSCardWrapper>
          ) : (
            callStatus && (
              <p className="text-brand font-medium text-sm">
                {callStatus}
              </p>
            )
          )}
        </div>
      </div>
    </DSModal>
  );
}
